import type {
  CreditAccount,
  DebitAccount,
  PaymentMethod,
  PurposeOfDebitAndCredit,
} from "@/app/types/payments";
import { Constants } from "@/app/utils/AppConstants";
import * as yup from "yup";

export const addNewBeneficiarySchema = yup.object().shape({
  // name: yup.string().required("Please Enter the Beneficiary Name"),
  name: yup
    .string()
    .required("Please Enter the Beneficiary Name")
    .min(2, "Beneficiary Name must be at least 2 characters")
    .max(50, "Beneficiary Name must not exceed 50 characters")
    .matches(
      /^[^\s].*[^\s]$/,
      "Beneficiary Name cannot start or end with space",
    )
    .matches(/^\p{L}+(?:\s\p{L}+)*$/u, "Only alphabets are allowed"),

  nickName: yup
    .string()
    .required("Please Enter the Nickname")
    .min(2, "Nickname must be at least 2 characters")
    .max(50, "Nickname must not exceed 50 characters"),

  email: yup
    .string()
    .required("Please Enter the Email Id")
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Please Enter a valid Email Id")
    .matches(
      /^(?!.*\.\.)(?!.*[._%+-]{2})[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/,
      "Sorry, your email cannot contain consecutive full stops (.)",
    )
    .matches(
      /^(?!^[._%+-])/,
      "Email address cannot start with a special character.",
    )
    .matches(/^(?!.*\.$)/, "Email address cannot end with a full stop.")
    .matches(
      /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/,
      "Email must be in lowercase only (no capital letters).",
    )
    .email("Please Enter a valid Email Id"),

  mobile: yup
    .string()
    .required("Please Enter the Mobile Number")
    .min(6, "Mobile Number must be at least 6 characters")
    .max(15, "Mobile Number must not exceed 15 characters"),

  countryDialCode: yup.string().required("Please select the country dial code"),
  address: yup
    .string()
    .required("Please Enter the Beneficiary Address")
    .min(5, "Beneficiary Address must be at least 5 characters"),

  account: yup
    .string()
    .required("Please Enter a Beneficiary Account/IBAN Number")
    .min(9, "Beneficiary Account/IBAN Number must be at least 9 characters"),
  bankCountry: yup
    .string()
    .required("Please Enter the Beneficiary Bank Country"),
  // .test(
  //   "min-length",
  //   "Beneficiary Bank Country must be more than 4 characters",
  //   (value) => !!value && value.trim().length > 4
  // ),
  // .min(4, "Beneficiary Bank Country must be at least 4 characters"),

  currency: yup
    .string()
    .required("Please Enter the Beneficiary Currency")
    .length(3, "Currency must be exactly 3 letters"),

  beneficiaryBankName: yup
    .string()
    .required("Please Enter the Beneficiary Bank Name")
    .min(4, "Beneficiary Bank Name must be at least 4 characters"),
  swiftCode: yup
    .string()
    .required("Please Enter the SWIFT/IFSC code")
    .min(4, "SWIFT/IFSC code must be at least 4 characters"),
  beneficiaryAddress: yup
    .string()
    .required("Please Enter the Beneficiary Bank Address")
    .min(5, "Beneficiary Bank Address must be at least 5 characters"),

  swiftClearingCode: yup.string().default(""),
});

export const domesticTransferSchema = (
  appRoutingType: string,
  purposeOfCredit: boolean,
) =>
  yup.object().shape({
    selectedAccount:
      appRoutingType === Constants.directBank
        ? yup.mixed<DebitAccount>().required("please_select_debit_account")
        : yup.mixed<DebitAccount>(),

    selectedBank:
      appRoutingType === Constants.directBank
        ? yup.mixed<DebitAccount>().required("please_select_debit_bank")
        : yup.mixed<DebitAccount>(),

    selectedPaymentMethod:
      appRoutingType === Constants.directBank
        ? yup.mixed<PaymentMethod>().required("please_select_payment_method")
        : yup.mixed<PaymentMethod>(),

    selectedBenifiary: yup
      .mixed<CreditAccount>()
      .required("please_select_beneficiary_account"),

    selectedOptions: yup.mixed().nullable(),

    paymentAmount: yup
      .string()
      .required("please_enter_amount")
      .test("is-valid-amount", "invalid_amount", (value) => {
        if (!value) return false;
        const amount = Number(value.replaceAll(",", ""));
        return amount > 0;
      }),

    purposeOfCredit:
      purposeOfCredit === true
        ? yup.string().required("please_select_purpose_of_credit")
        : yup.string(),
    purposeOfPaymentId: yup
      .number()
      .required("please_select_purpose_of_payment"),

    paymentDate: yup.string().required("please_select_payment_date"),

    enableTimeOfDebit: yup.boolean(),

    timeOfDebit: yup
      .string()
      .nullable()
      .when("enableTimeOfDebit", {
        is: true,
        then: (schema) => schema.required("please_select_payment_time"),
        otherwise: (schema) => schema.notRequired(),
      }),

    chargeAmount: yup.number().nullable(),

    internalRemark: yup.string().nullable(),

    whopays: yup.string().required("please_select_who_pays"),

    paymentReference: yup.string().required("please_enter_payment_reference"),

    paymentDetails: yup.string().nullable(),

    enableRecurringTransfer: yup.boolean(),

    recurringTransferStart: yup.string().when("enableRecurringTransfer", {
      is: true,
      then: (schema) => schema.required("please_select_start_date"),
      otherwise: (schema) => schema.notRequired(),
    }),

    recurringTransferEnd: yup.string().when("enableRecurringTransfer", {
      is: true,
      then: (schema) => schema.required("please_select_end_date"),
      otherwise: (schema) => schema.notRequired(),
    }),

    frequency: yup.string().when("enableRecurringTransfer", {
      is: true,
      then: (schema) => schema.required("please_select_frequency"),
      otherwise: (schema) => schema.notRequired(),
    }),

    executionDays: yup
      .array()
      .of(yup.number())
      .when("frequency", {
        is: "Weekly",
        then: (schema) => schema.min(1, "please_select_execution_days"),
        otherwise: (schema) => schema.notRequired(),
      }),

    executionDate: yup.string().when("frequency", {
      is: "Monthly",
      then: (schema) => schema.required("please_select_execution_date"),
      otherwise: (schema) => schema.notRequired(),
    }),

    selectedExecutionDays: yup.string().nullable(),

    description: yup.string().when("enableRecurringTransfer", {
      is: true,
      then: (schema) => schema.required("please_enter_description"),
      otherwise: (schema) => schema.notRequired(),
    }),

    enableSIB: yup.boolean().nullable(),
  });

export const internationalTransferSchema = (
  appRoutingType: string,
  purposeOfCredit: boolean,
) =>
  yup.object().shape({
    selectedAccount:
      appRoutingType === Constants.directBank
        ? yup.mixed<DebitAccount>().required("please_select_debit_account")
        : yup.mixed<DebitAccount>(),

    selectedBank:
      appRoutingType === Constants.directBank
        ? yup.mixed<DebitAccount>().required("please_select_debit_bank")
        : yup.mixed<DebitAccount>(),

    selectedPaymentMethod:
      appRoutingType === Constants.directBank
        ? yup.mixed<PaymentMethod>().required("please_select_payment_method")
        : yup.mixed<PaymentMethod>(),
    selectedBenifiary: yup
      .mixed()
      .required("please_select_beneficiary_account"),
    paymentAmount: yup.string().required("please_enter_amount"),
    theyAmount: yup.string().required("please_enter_amount"),
    purposeOfCredit:
      purposeOfCredit === true
        ? yup.string().required("please_select_purpose_of_credit")
        : yup.string(),
    purposeOfPayment: yup.string().required("please_select_purpose_of_payment"),
    paymentDate: yup.string().required("please_select_payment_date"),
    enableTimeOfDebit: yup.boolean(),
    timeOfDebit: yup.string().when("enableTimeOfDebit", {
      is: true,
      then: (schema) => schema.required("please_select_payment_time"),
      otherwise: (schema) => schema.notRequired(),
    }),
    paymentDetails: yup.string(),
    paymentReference: yup.string().required("please_enter_payment_reference"),
    chargeAmount: yup.number(),
    whopays: yup.string(),
    internalRemark: yup.string(),
    enableRecurringTransfer: yup.boolean(),
    recurringTransferStart: yup.string().when("enableRecurringTransfer", {
      is: true,
      then: (schema) => schema.required("please_select_start_date"),
      otherwise: (schema) => schema.notRequired(),
    }),
    recurringTransferEnd: yup.string().when("enableRecurringTransfer", {
      is: true,
      then: (schema) => schema.required("please_select_end_date"),
      otherwise: (schema) => schema.notRequired(),
    }),
    frequency: yup.string().when("enableRecurringTransfer", {
      is: true,
      then: (schema) => schema.required("please_select_frequency"),
      otherwise: (schema) => schema.notRequired(),
    }),
    selectedExecutionDays: yup.string().when("frequency", {
      is: "Weekly",
      then: (schema) => schema.required("please_select_execution_days"),
      otherwise: (schema) => schema.notRequired(),
    }),
    executionDate: yup.string().when("frequency", {
      is: "Monthly",
      then: (schema) => schema.required("please_select_execution_date"),
      otherwise: (schema) => schema.notRequired(),
    }),

    description: yup.string().when("enableRecurringTransfer", {
      is: true,
      then: (schema) => schema.required("please_enter_the_description"),
    }),

    // Weekly
    enableSIB: yup.boolean(),
  });

export const withinOwnAccountsSchema = (
  appRoutingType: string,
  purposeOfCredit: boolean,
) =>
  yup.object().shape({
    selectedPaymentMethod:
      appRoutingType === Constants.directBank
        ? yup.mixed().required("please_select_payment_method")
        : yup.mixed(),
    selectedAccount:
      appRoutingType === Constants.directBank
        ? yup.mixed().required("please_select_debit_account")
        : yup.mixed(),
    selectedBank:
      appRoutingType === Constants.directBank
        ? yup.mixed().required("please_select_debit_bank")
        : yup.mixed(),
    selectedBenifiary: yup.mixed().required("please_select_credit_account"),
    paymentAmount: yup.string().required("please_enter_amount"),
    purposeOfCredit:
      purposeOfCredit === true
        ? yup.string().required("please_select_purpose_of_credit")
        : yup.string(),
    purposeOfPaymentId: yup
      .number()
      .required("please_select_purpose_of_payment"),
    paymentDate: yup.string().required("please_select_payment_date"),
    enableTimeOfDebit: yup.boolean(),
    timeOfDebit: yup.string().when("enableTimeOfDebit", {
      is: true,
      then: (schema) => schema.required("please_select_payment_time"),
      otherwise: (schema) => schema.notRequired(),
    }),

    paymentDetails: yup.string(),

    internalRemark: yup.string(),

    recurringTransfer: yup.boolean(),

    recurringTransferStart: yup.string().when("recurringTransfer", {
      is: true,
      then: (schema) => schema.required("please_select_start_date"),
    }),
    recurringTransferEnd: yup.string().when("recurringTransfer", {
      is: true,
      then: (schema) => schema.required("please_select_end_date"),
    }),
    frequency: yup.string().when("recurringTransfer", {
      is: true,
      then: (schema) => schema.required("please_select_frequency"),
    }),

    executionDays: yup.string().when("frequency", {
      is: "Weekly",
      then: (schema) => schema.required("please_select_execution_days"),
      otherwise: (schema) => schema.notRequired(),
    }),

    executionDate: yup.string().when("frequency", {
      is: (val: string) => val?.toLowerCase() === "monthly",
      then: (schema) => schema.required("please_select_execution_date"),
      otherwise: (schema) => schema.notRequired(),
    }),
    description: yup.string().when("recurringTransfer", {
      is: true,
      then: (schema) => schema.required("please_enter_the_description"),
    }),
  });

export const aaniPaymentSchema = (appRoutingType: string, step: number) =>
  yup.object().shape({
    paymentMethod:
      appRoutingType === Constants.directBank
        ? yup.mixed<PaymentMethod>().required("please_select_payment_method")
        : yup.mixed<PaymentMethod>(),
    ...(appRoutingType === Constants.centralBank && step === 2
      ? {
          debitOption: yup.number().required(),
        }
      : { debitOption: yup.number() }),
    ...(appRoutingType === Constants.centralBank && step === 2
      ? {
          debitBank: yup.mixed<DebitAccount>().when("debitOption", {
            is: (val: number) => val === 2,
            then: (schema) => schema.required("please_select_debit_bank"),
            otherwise: (schema) => schema.notRequired(),
          }),
        }
      : { debitBank: yup.mixed<DebitAccount>() }),
    debitAccount:
      appRoutingType === Constants.directBank
        ? yup.mixed<DebitAccount>().required("please_select_debit_account")
        : yup.mixed<DebitAccount>(),
    debitAmount: yup.string().required("please_enter_amount"),
    isBalanceSufficient: yup.boolean().required(),
    isAmountExceedsMaxAmount: yup.boolean().required(),
    useEmail: yup.boolean().required(),
    creditAccount: yup
      .string()
      .defined()
      .when("useEmail", {
        is: true,
        then: (schema) =>
          schema.email("please_enter_valid_email").required("email_is_reqired"),
        otherwise: (schema) => schema.required("please_enter_mobile_number"),
      }),
    paymentDate: yup.string().required("please_select_payment_date"),
    note: yup.string(),
    countryDialCode: yup.string().required(),
    thresholdAmount: yup.string(),
    consentValidity: yup.number(),
  });

export const UserSchema = yup.object().shape({
  firstName: yup
    .string()
    .required("First Name is required.")
    .min(3, "First Name must be at least 3 characters.")
    .max(30, "First Name cannot exceed 30 characters."),

  lastName: yup
    .string()
    .required("Last Name is required.")
    .min(3, "Last Name must be at least 3 characters.")
    .max(30, "Last Name cannot exceed 30 characters."),

  email: yup
    .string()
    .required("Email is required.")
    // .email("Please Enter a valid email address."),
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/, "Enter a valid email address"),

  mobileNumber: yup
    .string()
    .required("Mobile number is required.")
    .matches(/^\d{9}$/, "Mobile number must contain exactly 9 digits."),

  idpro: yup.object().shape({
    idtype: yup.object().required("ID Type is required."),
    number: yup
      .string()
      .required("ID Number is required.")
      .test("validate-id-number", "Invalid ID Number format", function (value) {
        const { idtype } = this.parent;
        if (!idtype) return false;

        if (idtype.label === "Emirate ID") {
          const digitsOnly = (value ?? "").replaceAll(/\D/g, "");
          return digitsOnly.length === 15;
        } else if (idtype.label === "Passport ID") {
          const cleanValue = (value ?? "")
            .toUpperCase()
            .replaceAll(/[^A-Z0-9]/g, "");
          return cleanValue.length >= 7 && cleanValue.length <= 9;
        }
        return false;
      }),
  }),

  role: yup.string().required("Role is required."),
  designation: yup.string().required("Designation is required."),
});

// ─── NEW: adminProfileSchema ───────────────────────────────────────────────
// Standalone validation schema for the "admin profile" fields owned by
// InviteCreate / AdminEditProfile (firstName, lastName, email, mobileNumber,
// idpro). Unlike UserSchema, this does NOT require role/designation, since
// InviteCreate's create-flow form doesn't own those fields — this schema
// covers exactly what that form validates, so it can be used directly
// without needing .pick()/.omit() against the shared UserSchema.

// This is the adminProfileSchema extract only — merge this into your
// existing ValidationSchema.ts (it likely has other schemas alongside it,
// which weren't in the file you shared, so only this block is reproduced
// here). Everything below is unchanged from your original except the
// `country`, `role`, `whatsappEnabled`, and `whatsappNumber` fields, which
// are new.

export const adminProfileSchema = yup.object().shape({
  firstName: yup
    .string()
    .required("First Name is required.")
    .min(3, "First Name must be at least 3 characters.")
    .max(30, "First Name cannot exceed 30 characters."),

  lastName: yup
    .string()
    .required("Last Name is required.")
    .min(3, "Last Name must be at least 3 characters.")
    .max(30, "Last Name cannot exceed 30 characters."),

  email: yup
    .string()
    .required("Email is required.")
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/, "Enter a valid email address"),

  mobileNumber: yup
    .string()
    .required("Mobile number is required.")
    .test(
      "mobile-length",
      "Mobile number must contain exactly 9 digits.",
      (value) => (value ?? "").replaceAll(/\D/g, "").length === 9,
    ),

  idpro: yup.object().shape({
    idtype: yup.object().required("ID Type is required."),
    number: yup
      .string()
      .required("ID Number is required.")
      .test("validate-id-number", "Invalid ID Number format", function (value) {
        const { idtype } = this.parent;
        if (!idtype) return false;

        const label = (idtype.label ?? "").toLowerCase();

        if (label.includes("emirate")) {
          const digitsOnly = (value ?? "").replaceAll(/\D/g, "");
          return digitsOnly.length === 15;
        } else if (label.includes("passport")) {
          const cleanValue = (value ?? "")
            .toUpperCase()
            .replaceAll(/[^A-Z0-9]/g, "");
          return cleanValue.length >= 7 && cleanValue.length <= 9;
        }
        return false;
      }),
  }),

  // ── NEW: country select dropdown ──────────────────────────────────────
  country: yup.string().required("Please select a country."),

  // ── NEW: role dropdown ────────────────────────────────────────────────
  role: yup
    .object()
    .nullable()
    .required("Role is required.")
    .test("role-selected", "Role is required.", (val: any) => !!val?.value),

  // ── NEW: WhatsApp number — only required when whatsappEnabled is true ──
  whatsappEnabled: yup.boolean(),

  whatsappNumber: yup.string().when("whatsappEnabled", {
    is: true,
    then: (schema) =>
      schema
        .required("WhatsApp number is required.")
        .test(
          "whatsapp-length",
          "WhatsApp number must contain exactly 9 digits.",
          (value) => (value ?? "").replaceAll(/\D/g, "").length === 9,
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const allTransferSchemaForCentralBank = (countries: string[]) =>
  yup.object().shape({
    selectedPaymentMethod: yup.mixed<PaymentMethod>().nullable().defined(),
    // selectedBank: yup.mixed<DebitAccount>().defined(),
    isOpenBeneficiaryDrawer: yup.boolean().defined(),
    selectedBeneficiary: yup
      .mixed<CreditAccount>()
      .required("please_select_credit_account"),

    purposeOfCredit: yup
      .mixed<PurposeOfDebitAndCredit>()
      .defined()
      .when("selectedBeneficiary", {
        is: (val: CreditAccount) => countries.includes(val?.beneCountryCode2),
        then: (schema) => schema.required("please_select_purpose_of_credit"),
      }),
    isOpenExecutionDaysDrawer: yup.boolean().defined(),
    paymentAmount: yup.string().required("please_enter_amount"),
    creditAmount: yup.string().required("please_enter_amount"),
    creditCountryCode: yup.string().defined(),
    fxRate: yup.number().defined(),

    // recurringTransfer: yup.boolean(),

    // recurringTransferStart: yup.string().when("recurringTransfer", {
    //   is: true,
    //   then: (schema) => schema.required("please_select_start_date"),
    // }),
    // recurringTransferEnd: yup.string().when("recurringTransfer", {
    //   is: true,
    //   then: (schema) => schema.required("please_select_end_date"),
    // }),
    // frequency: yup.string().when("recurringTransfer", {
    //   is: true,
    //   then: (schema) => schema.required("please_select_frequency"),
    // }),

    // executionDay: yup.string().when("frequency", {
    //   is: "Weekly",
    //   then: (schema) => schema.required("please_select_execution_days"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),

    // executionDate: yup.string().when("frequency", {
    //   is: (val: string) => val?.toLowerCase() === "monthly",
    //   then: (schema) => schema.required("please_select_execution_date"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
    // description: yup.string().when("recurringTransfer", {
    //   is: true,
    //   then: (schema) => schema.required("please_enter_the_description"),
    // }),
  });

export const allTransferSchemaDirectBank = (countries: string[]) =>
  yup.object().shape({
    selectedPaymentMethod: yup
      .mixed<PaymentMethod>()
      .required("please_select_payment_method"),
    isOpenPaymentMethodDrawer: yup.boolean().defined(),
    isOpenBankDrawer: yup.boolean().defined(),
    isOpenDebitAccountDrawer: yup.boolean().defined(),
    selectedDebitAccount: yup
      .mixed<DebitAccount>()
      .required("please_select_debit_account"),
    selectedBank: yup
      .mixed<DebitAccount>()
      .defined()
      .required("please_select_debit_bank"),
    isOpenBeneficiaryDrawer: yup.boolean().defined(),
    selectedBeneficiary: yup
      .mixed<CreditAccount>()
      .required("please_select_credit_account"),
    isOpenExecutionDaysDrawer: yup.boolean().defined(),
    purposeOfCredit: yup
      .mixed<PurposeOfDebitAndCredit>()
      .defined()
      .when("selectedBeneficiary", {
        is: (val: CreditAccount) => countries.includes(val?.beneCountryCode2),
        then: (schema) => schema.required("please_select_purpose_of_credit"),
      }),
    paymentAmount: yup.string().required("please_enter_amount"),
    creditAmount: yup.string().required("please_enter_amount"),
    creditCountryCode: yup.string().defined(),
    fxRate: yup.number().defined(),

    // recurringTransfer: yup.boolean(),
    // recurringTransferStart: yup.string().when("recurringTransfer", {
    //   is: true,
    //   then: (schema) => schema.required("please_select_start_date"),
    // }),
    // recurringTransferEnd: yup.string().when("recurringTransfer", {
    //   is: true,
    //   then: (schema) => schema.required("please_select_end_date"),
    // }),
    // frequency: yup.string().when("recurringTransfer", {
    //   is: true,
    //   then: (schema) => schema.required("please_select_frequency"),
    // }),

    // executionDay: yup.string().when("frequency", {
    //   is: "Weekly",
    //   then: (schema) => schema.required("please_select_execution_days"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),

    // executionDate: yup.string().when("frequency", {
    //   is: (val: string) => val?.toLowerCase() === "monthly",
    //   then: (schema) => schema.required("please_select_execution_date"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
    // description: yup.string().when("recurringTransfer", {
    //   is: true,
    //   then: (schema) => schema.required("please_enter_the_description"),
    // }),
  });

export const UserNameschema = yup.object().shape({
  firstname: yup.string().required("Please Enter the Firth Name"),
  lastname: yup.string().required("Please Enter Last Name"),
});

export const MagnatiSchema = yup.object().shape({
  username: yup
    .string()
    .required("Please enter the User Name")
    .matches(
      /^[A-Za-z0-9_]+$/,
      "Only letters, numbers, and underscores are allowed",
    )
    .min(3, "User Name must be at least 3 characters")
    .max(20, "User Name cannot exceed 20 characters"),

  password: yup
    .string()
    .required("Please enter the Password")
    .min(8, "Password must be at least 8 characters")
    .max(32, "Password cannot exceed 32 characters"),

  merchant: yup
    .string()
    .required("Please enter the Merchant Name")
    .matches(/^[A-Za-z\s]+$/, "Only alphabets and spaces are allowed")
    .min(2, "Merchant Name must contain at least 2 letters")
    .max(50, "Merchant Name cannot exceed 50 characters"),

  storeId: yup
    .string()
    .required("Please enter the Store ID")
    .matches(/^[A-Z0-9]+$/, "Store ID must be alphanumeric (uppercase only)")
    .min(3, "Store ID must be at least 3 characters")
    .max(20, "Store ID cannot exceed 20 characters"),

  terminalId: yup
    .string()
    .required("Please enter the Terminal ID")
    .matches(/^[A-Z0-9]+$/, "Terminal ID must be alphanumeric (uppercase only)")
    .min(3, "Terminal ID must be at least 3 characters")
    .max(15, "Terminal ID cannot exceed 15 characters"),
});

export const PosSchema = yup.object().shape({
  username: yup
    .string()
    .required("Please enter the User Name")
    .matches(
      /^[A-Za-z0-9_]+$/,
      "Only letters, numbers, and underscores allowed",
    )
    .min(3, "User Name must be at least 3 characters")
    .max(20, "User Name cannot exceed 20 characters"),

  password: yup
    .string()
    .required("Please enter the Password")
    .min(8, "Password must be at least 8 characters")
    .max(32, "Password cannot exceed 32 characters"),

  merchant: yup
    .string()
    .required("Please enter the Merchant Name")
    .matches(/^[A-Za-z\s]+$/, "Only alphabets and spaces allowed")
    .min(2, "Merchant Name must contain at least 2 letters")
    .max(50, "Merchant Name cannot exceed 50 characters"),

  storeId: yup
    .string()
    .required("Please enter the Store ID")
    .matches(/^[A-Z0-9]+$/, "Store ID must be uppercase alphanumeric")
    .min(3, "Store ID must be at least 3 characters")
    .max(20, "Store ID cannot exceed 20 characters"),

  terminalId: yup
    .string()
    .required("Please enter the Terminal ID")
    .matches(/^[A-Z0-9]+$/, "Terminal ID must be uppercase alphanumeric")
    .min(3, "Terminal ID must be at least 3 characters")
    .max(15, "Terminal ID cannot exceed 15 characters"),

  mobile: yup
    .string()
    .required("Please enter the POC Contact Number")
    .matches(/^[0-9]+$/, "Only digits allowed")
    .min(10, "Mobile number must be at least 10 digits")
    .max(15, "Mobile number cannot exceed 15 digits"),

  tradeLicenseNumber: yup
    .string()
    .required("Please enter the Trade License Number")
    .matches(/^[A-Za-z0-9]+$/, "Only alphanumeric values allowed")
    .min(5, "Trade License Number must be at least 5 characters")
    .max(30, "Trade License Number cannot exceed 30 characters"),

  tradeLicenseFile: yup
    .mixed()
    .required("Please upload the Trade License File"),
});

export const rtpPay = (appRoutingType: string) =>
  yup.object().shape({
    selectedAccount:
      appRoutingType === Constants.directBank
        ? yup.mixed<DebitAccount>().required("please_select_debit_account")
        : yup.mixed<DebitAccount>(),

    selectedBank:
      appRoutingType === Constants.directBank
        ? yup.mixed<DebitAccount>().required("please_select_debit_bank")
        : yup.mixed<DebitAccount>(),

    selectedPaymentMethod:
      appRoutingType === Constants.directBank
        ? yup.mixed<PaymentMethod>().required("please_select_payment_method")
        : yup.mixed<PaymentMethod>(),

    selectedBenifiary: yup
      .mixed<CreditAccount>()
      .required("please_select_beneficiary_account"),

    selectedOptions: yup.mixed().nullable(),

    paymentAmount: yup
      .string()
      .required("please_enter_amount")
      .test("is-valid-amount", "invalid_amount", (value) => {
        if (!value) return false;
        const amount = Number(value.replaceAll(",", ""));
        return amount > 0;
      }),

    purposeOfPaymentId: yup
      .number()
      .required("please_select_purpose_of_payment"),

    paymentDate: yup.string().required("please_select_payment_date"),

    // enableTimeOfDebit: yup.boolean(),

    // timeOfDebit: yup
    //   .string()
    //   .nullable()
    //   .when("enableTimeOfDebit", {
    //     is: true,
    //     then: (schema) => schema.required("please_select_payment_time"),
    //     otherwise: (schema) => schema.notRequired(),
    //   }),

    // chargeAmount: yup.number().nullable(),

    // internalRemark: yup.string().nullable(),

    // whopays: yup.string().required("please_select_who_pays"),

    // paymentReference: yup.string().required("please_enter_payment_reference"),

    // paymentDetails: yup.string().nullable(),

    // enableRecurringTransfer: yup.boolean(),

    // recurringTransferStart: yup.string().when("enableRecurringTransfer", {
    //   is: true,
    //   then: (schema) => schema.required("please_select_start_date"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),

    // recurringTransferEnd: yup.string().when("enableRecurringTransfer", {
    //   is: true,
    //   then: (schema) => schema.required("please_select_end_date"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),

    // frequency: yup.string().when("enableRecurringTransfer", {
    //   is: true,
    //   then: (schema) => schema.required("please_select_frequency"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),

    // executionDays: yup
    //   .array()
    //   .of(yup.number())
    //   .when("frequency", {
    //     is: "Weekly",
    //     then: (schema) => schema.min(1, "please_select_execution_days"),
    //     otherwise: (schema) => schema.notRequired(),
    //   }),

    // executionDate: yup.string().when("frequency", {
    //   is: "Monthly",
    //   then: (schema) => schema.required("please_select_execution_date"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),

    // selectedExecutionDays: yup.string().nullable(),

    // description: yup.string().when("enableRecurringTransfer", {
    //   is: true,
    //   then: (schema) => schema.required("please_enter_description"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),

    // enableSIB: yup.boolean().nullable(),
  });
